import {
  PermissionEnum,
  UserTypeEnum,
  Workspace,
} from '@sr-sdks/api-sdk-axios';
import { SR_ICONS } from '@sr/ui-library';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useEffect } from 'react';
import { Dropdown, Navbar } from 'react-daisyui';
import { useNavigate } from 'react-router-dom';
import { queryOptionsGetWorkspace } from '../../api/workspaces/workspaceApi';
import { MSR3_HOME_FLAGS } from '../../constant/featureFlags';
import { useCurrentUserAuth } from '../../hooks/useCurrentUserAuth';
import { workspaceService } from '../../reducers/workspaces/workspaceSlice';
import { useAppDispatch, useTypedSelector } from '../../stateStore';
import { ManageWorkspacePermissions } from '../../types/workspaceTypes';
import {
  isInternalUser,
  userInitials,
  workspaceTypeIsAgent,
} from '../../utils/helpers/common';
import { MigrationSwitch } from '../MigrationSwitch';
import ProtectElement from '../ProtectElement';
import AppSidebarMenu from './AppSidebarMenu';

interface IWorkspaceSidebar {
  workspaceId?: string;
}

export const WorkspaceSidebar = ({ workspaceId }: IWorkspaceSidebar) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Use TanStack Query for workspace data
  const { data: workspace } = useQuery(queryOptionsGetWorkspace(workspaceId));
  const { auth, currentUser } = useCurrentUserAuth();

  const workspaces = useTypedSelector(workspaceService.selectors.workspaces);
  const isUpdating = useTypedSelector(workspaceService.selectors.isUpdating);

  useEffect(() => {
    if (
      auth?.currentUserHasPermission(PermissionEnum.ViewWorkspace) &&
      currentUser?.userType === UserTypeEnum.External
    ) {
      dispatch(workspaceService.findAll());
    }
  }, [auth, currentUser, dispatch, isUpdating]);

  const handleSelectWorkspace = (workspace: Workspace) => {
    const element = document.activeElement as HTMLElement;

    // To close the dropdown when selecting workspace
    if (element) {
      element.blur();
    }

    navigate(`/workspace/${workspace._id}`, {
      replace: true,
    });
  };

  return (
    <Navbar className="flex-col p-0">
      <Navbar.Start className="modal-avatar block w-full">
        <div className="flex flex-col gap-2 self-stretch">
          <span className="font-sans text-xs font-normal leading-normal text-sr-white">
            Workspace
          </span>
          <Dropdown
            className={clsx('w-full', {
              'pointer-events-none cursor-not-allowed':
                isInternalUser(currentUser),
            })}
          >
            <Dropdown.Toggle
              button={false}
              className="btn btn-ghost flex h-full flex-col items-start justify-center gap-2 self-stretch rounded-md border border-sr-light-gray px-3 py-2 pb-1.5 pt-1.5 hover:border-sr-white"
            >
              <div className="flex flex-[1_0_0] gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full border border-sr-outline bg-white px-3 py-2 font-sans text-sm font-normal leading-6 text-sr-black">
                  {userInitials(workspace?.workspaceName ?? '')}
                </div>
                <div className="flex flex-col items-start justify-center text-left">
                  <div className="w-40 break-words font-sans text-sm font-bold leading-normal text-sr-white">
                    {workspace?.workspaceName}
                  </div>
                </div>
              </div>
            </Dropdown.Toggle>
            <Dropdown.Menu className="workspaces z-50 inline-block max-h-[600px] w-80 min-w-80 overflow-y-auto rounded-lg border border-sr-white text-sr-black">
              {workspaces.map((workspace) => (
                <Dropdown.Item
                  key={workspace._id}
                  className="flex items-center self-stretch px-3 py-[6px]"
                  onClick={() => handleSelectWorkspace(workspace)}
                >
                  <div className="flex items-center gap-3 self-stretch">
                    <div className="flex h-9 w-9 min-w-9 items-center justify-center rounded-full border border-sr-outline bg-white p-2 text-sm text-sr-black">
                      {userInitials(workspace.workspaceName ?? '')}
                    </div>
                    <div className="w-48 break-words text-sm text-sr-black">
                      {workspace.workspaceName}
                    </div>
                  </div>
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <AppSidebarMenu.Section className="mt-7">
          <MigrationSwitch
            featureFlags={MSR3_HOME_FLAGS}
            newComponent={
              <AppSidebarMenu.Item
                label="Home"
                icon={SR_ICONS.house}
                linkTo={`/workspace/${workspaceId}/home`}
              />
            }
            oldComponent={null}
          />
          <AppSidebarMenu.Item
            label="Titles"
            icon={SR_ICONS.filmStrip}
            linkTo={`/workspace/${workspaceId}/titles`}
          />
          <AppSidebarMenu.Item
            label="Payments"
            icon={SR_ICONS.wallet}
            linkTo={`/workspace/${workspaceId}/payments`}
          />
          {workspaceTypeIsAgent(workspace) && (
            <ProtectElement
              permissionRequired={[PermissionEnum.ViewClients]}
              workspaceId={workspace?._id}
            >
              <AppSidebarMenu.Item
                label="Clients"
                icon={SR_ICONS.usersThree}
                linkTo={`/workspace/${workspaceId}/clients`}
              />
            </ProtectElement>
          )}
          <ProtectElement
            permissionRequired={ManageWorkspacePermissions}
            workspaceId={workspace?._id}
          >
            <AppSidebarMenu.Item
              label="Settings"
              icon={SR_ICONS.gear}
              linkTo={`/workspace/${workspaceId}/manage-workspace`}
            />
          </ProtectElement>
        </AppSidebarMenu.Section>
      </Navbar.Start>
    </Navbar>
  );
};
